import {
  Checkbox,
  CountBadge,
  DataTable,
  LeaderGroupCombobox,
  ModuleWrapper,
  StickyFilters,
  toast,
  UserCombobox,
} from '@/components'
import { Button } from '@/components_deprecated'
import { Input } from '@/components_deprecated/Input'
import { Tabs, type Tab } from '@/components_deprecated/Tabs/Tabs'
import type { ExportFormat } from '@/modules/exports'
import {
  useBreadcrumbOverride,
  useNodes,
  useOnErrorAuto,
  useParam,
  useUser,
} from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Link, Navigate } from 'react-router'
import styled from 'styled-components'
import { EXPORT_DIFFERENCE_SALARY_BASE, EXPORT_SALARY_BASE } from './mutations'
import {
  DIFFERENCE_SALARY_BASE_BY_MONTH,
  DIFFERENCE_SALARY_BASE_ENTRIES,
  SALARY_BASE,
  SALARY_BASE_ENTRIES,
} from './queries'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { ExportSalaryBaseEntriesPrompt } from '../companies/ExportSalaryBaseEntriesPrompt'
import { format } from '@/utils'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

const TableFilter = styled.div`
  display: flex;
  margin-bottom: 1rem;
  padding: 1rem 0;
  background-color: ${props => props.theme.colors.white};
  border-radius: 16px;

  ${props => props.theme.layout.mobile} {
    & > div {
      width: 100%;
      padding: 1rem;
    }
    flex-direction: column;
  }
`

const FilterBlock = styled.div`
  flex-basis: 0;
  flex: 1 1 0px;
  padding: 0 1rem;
  & > label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    width: 100%;
    color: ${props => props.theme.colors.gray5};
  }
  ${props => props.theme.layout.mobile} {
    display: flex;
    flex-direction: column;
  }
`

interface SalaryEntryRow {
  id: string
  createdAt?: string | null
  user: { id: string; fullName: string; internalNumber?: string | null }
  initialAmount: number
  amount: number
  departmentId: string
  facilityId: string
  salaryId: string
  exportTimestamp?: string | null
}

const TAB_BASE = 'base'
const TAB_DIFFERENCE = 'difference'

export const SalaryBase: React.JSX.CFC = () => {
  const translations = useTranslate({
    name: 'common.name',
    created: 'common.created',
    edit: 'common.edit',
    delete: 'common.delete',
    user: 'common.user',
    leaderGroups: 'companies.leader-groups',
    noData: 'common.no-data',

    filter: 'common.filter',

    userFilter: 'common.user-filter',
    departmentFilter: 'common.department-filter',
    facility: 'common.facility',
    salaryCodeFilter: 'common.salary-code-filter',

    salaryBase: 'common.salary-base',
    salaryBaseLines: 'salary.salary-base-lines',
    differences: 'salary.differences',
    externalId: 'common.external-id',
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    initialAmount: 'salary.initial-amount',
    department: 'common.department',
    export: 'common.export',
    onlyNonExported: 'salary.only-non-exported',
    items: ['salary.table.num-items', { n: 0 }],
  })

  const salaryBaseId = useParam('salaryBaseId')
  const me = useUser()

  const [activeTabKey, setActiveTabKey] = useState<string>(TAB_BASE)
  const isDifference = activeTabKey === TAB_DIFFERENCE

  const { formValues, formChangeHandler: handler } = useForm<{
    user: string
    leaderGroups: string[]
    onlyNonExported: boolean
    orderBy: string
  }>({
    values: {
      user: '',
      leaderGroups: [],
      onlyNonExported: false,
      orderBy: 'user__lastName',
    },
  })

  const onErrorAuto = useOnErrorAuto()
  const addPrompt = usePrompt()

  const {
    data,
    loading,
    refetch: refetchSalaryBase,
  } = useQuery(SALARY_BASE, {
    variables: {
      id: salaryBaseId,
    },
    fetchPolicy: 'cache-and-network',
  })

  const salaryBase = data?.salaryBase

  const tabs = useMemo<[Tab, Tab]>(
    () => [
      {
        key: TAB_BASE,
        text: (
          <span className="inline-flex items-center gap-2">
            {translations.salaryBase}
            <CountBadge count={salaryBase?.unexportedEntryCount ?? 0} />
          </span>
        ),
      },
      {
        key: TAB_DIFFERENCE,
        text: (
          <span className="inline-flex items-center gap-2">
            {translations.differences}
            <CountBadge count={salaryBase?.unexportedDifferenceCount ?? 0} />
          </span>
        ),
      },
    ],
    [
      translations.salaryBase,
      translations.differences,
      salaryBase?.unexportedEntryCount,
      salaryBase?.unexportedDifferenceCount,
    ]
  )

  const {
    data: entryData,
    loading: entryLoading,
    refetch,
  } = useQuery(SALARY_BASE_ENTRIES, {
    variables: {
      id: salaryBaseId,
      user: formValues.user ?? undefined,
      leaderGroups: formValues.leaderGroups ?? undefined,
      onlyNonExported: formValues.onlyNonExported,
      orderBy: formValues.orderBy,
    },
    fetchPolicy: 'cache-and-network',
  })

  // The difference base is keyed by month, not by the salary base id.
  const { data: diffBaseData } = useQuery(DIFFERENCE_SALARY_BASE_BY_MONTH, {
    variables: {
      year: salaryBase?.year,
      month: salaryBase?.month,
    },
    skip: !salaryBase?.year || !salaryBase?.month,
    fetchPolicy: 'cache-and-network',
  })
  const differenceBase = diffBaseData?.differenceSalaryBaseByMonth

  const {
    data: diffEntryData,
    loading: diffEntryLoading,
    refetch: refetchDiff,
  } = useQuery(DIFFERENCE_SALARY_BASE_ENTRIES, {
    variables: {
      id: differenceBase?.id ?? '',
      user: formValues.user ?? undefined,
      leaderGroups: formValues.leaderGroups ?? undefined,
      onlyNonExported: formValues.onlyNonExported,
      orderBy: formValues.orderBy,
    },
    skip: !differenceBase?.id,
    fetchPolicy: 'cache-and-network',
  })

  const [exportSalaryBase, { loading: exportLoading }] = useMutation(
    EXPORT_SALARY_BASE,
    {
      onError: onErrorAuto(),
    }
  )

  const [exportDifferenceSalaryBase, { loading: exportDiffLoading }] =
    useMutation(EXPORT_DIFFERENCE_SALARY_BASE, {
      onError: onErrorAuto(),
    })

  const salaryBaseDate = new Date(
    salaryBase?.year ?? 0,
    (salaryBase?.month ?? 1) - 1
  )

  const salaryBaseTitle =
    translations.salaryBase + ` ${format(salaryBaseDate, 'MMMM yyyy')}`

  useBreadcrumbOverride(salaryBaseId, salaryBaseTitle)

  const entries = useNodes(
    entryData?.allSalaryBaseEntries
  ) as unknown as SalaryEntryRow[]
  const differenceEntries = useNodes(
    diffEntryData?.allDifferenceSalaryBaseEntries
  ) as unknown as SalaryEntryRow[]

  async function handleExport() {
    const { data } = await addPrompt<{
      exportFormat: ExportFormat
      leaderGroups: string[]
      onlyNonExported: boolean
    } | null>(resolve => (
      <ExportSalaryBaseEntriesPrompt
        salaryBaseId={salaryBaseId}
        onSubmit={resolve}
        initialLeaderGroups={formValues.leaderGroups}
        initialOnlyNonExported={formValues.onlyNonExported}
      />
    ))
    if (!data) return

    const result = await exportSalaryBase({
      variables: {
        salaryBase: salaryBaseId,
        user: me.id,
        exportFormat: data.exportFormat,
        leaderGroups: data.leaderGroups,
        onlyNonExported: data.onlyNonExported,
      },
    })
    if (result.data?.exportSalaryBase?.ok) {
      toast.export(result.data.exportSalaryBase.exportId ?? null)
    }
    void refetch()
    void refetchSalaryBase()
  }

  async function handleExportDifference() {
    if (!differenceBase?.id) return

    const { data } = await addPrompt<{
      exportFormat: ExportFormat
      leaderGroups: string[]
      onlyNonExported: boolean
    } | null>(resolve => (
      <ExportSalaryBaseEntriesPrompt
        salaryBaseId={differenceBase.id}
        isDifference
        onSubmit={resolve}
        initialLeaderGroups={formValues.leaderGroups}
        initialOnlyNonExported={formValues.onlyNonExported}
      />
    ))
    if (!data) return

    const result = await exportDifferenceSalaryBase({
      variables: {
        differenceSalaryBase: differenceBase.id,
        user: me.id,
        exportFormat: data.exportFormat,
        leaderGroups: data.leaderGroups,
        onlyNonExported: data.onlyNonExported,
      },
    })
    if (result.data?.exportDifferenceSalaryBase?.ok) {
      toast.export(result.data.exportDifferenceSalaryBase.exportId ?? null)
    }
    void refetchDiff()
    void refetchSalaryBase()
  }

  const column = createColumnHelper<SalaryEntryRow>()

  const tableColumns = useMemo(
    () => [
      column.accessor('user', {
        header: translations.user,
        enableSorting: true,
        cell(info) {
          const user = info.getValue()
          const userText = user.internalNumber
            ? `(${user.internalNumber}) ${user.fullName}`
            : user.fullName

          return (
            <Link
              className="text-inherit"
              to={`/users/${user.id}`}
              target="_blank"
            >
              {userText}
            </Link>
          )
        },
      }),
      column.accessor('departmentId', {
        header: translations.department,
        enableSorting: true,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('facilityId', {
        header: translations.facility,
        enableSorting: true,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('salaryId', {
        enableSorting: true,
        header: () => translations.externalId,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('amount', {
        header: translations.amount,
        enableSorting: true,
        sortingFn: (a, b) => a.original.amount - b.original.amount,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue().toString()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('exportTimestamp', {
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const exported = !!info.getValue()
          return (
            <td className="text-center">
              <Icon
                icon={exported ? 'check' : 'close'}
                color={exported ? 'green' : 'red'}
              />
            </td>
          )
        },
      }),
    ],
    [column, translations]
  )

  // Difference tab accumulates one row per diff run, so show when each layer was
  // recorded to tell the runs apart.
  const differenceColumns = useMemo(
    () => [
      column.accessor('createdAt', {
        header: translations.created,
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.createdAt ?? 0).getTime() -
          new Date(b.original.createdAt ?? 0).getTime(),
        cell(info) {
          const value = info.getValue()
          return (
            <td>{value ? format(new Date(value), 'dd.MM.yyyy HH:mm') : ''}</td>
          )
        },
      }),
      ...tableColumns,
    ],
    [column, tableColumns, translations]
  )

  if (!loading && !salaryBase) return <Navigate to="/salary-bases" />

  const tableData = isDifference ? differenceEntries : entries
  const columns = isDifference ? differenceColumns : tableColumns
  const tableLoading = isDifference ? diffEntryLoading : entryLoading
  const exportDisabled = isDifference
    ? exportDiffLoading || !differenceBase?.id
    : exportLoading
  const onExport = isDifference ? handleExportDifference : handleExport

  return (
    <ModuleWrapper
      title={salaryBaseTitle}
      loading={loading}
      headerRight={
        !tableLoading && (
          <Button onClick={onExport} disabled={exportDisabled}>
            {translations.export}
          </Button>
        )
      }
    >
      <Tabs
        activeTab={tabs.find(t => t.key === activeTabKey) ?? tabs[0]}
        tabs={tabs}
        onChange={tab => setActiveTabKey(tab.key)}
      />

      <StickyFilters>
        <TableFilter>
          <FilterBlock>
            <label>{translations.userFilter}</label>
            <UserCombobox
              mode="single"
              value={formValues.user}
              filterSalaryCalculation
              onChange={handler('user')}
            />
          </FilterBlock>

          <FilterBlock>
            <label>{translations.leaderGroups}</label>
            <LeaderGroupCombobox
              mode="multiple"
              value={formValues.leaderGroups}
              onChange={handler('leaderGroups')}
            />
          </FilterBlock>
          <FilterBlock>
            <label>{translations.onlyNonExported}</label>
            <Checkbox
              value={formValues.onlyNonExported}
              onChange={handler('onlyNonExported')}
            />
          </FilterBlock>
        </TableFilter>
      </StickyFilters>
      <DataTable
        columns={columns}
        data={tableData}
        pagination={{
          itemsText: n => translations.items({ n }),
          pageSize: 20,
        }}
        initialSort={[{ id: 'user', desc: false }]}
        inCard
      />
    </ModuleWrapper>
  )
}
